/**
 * The one HTTP client. Every API call in the app goes through here.
 *
 * Responsibilities, each in exactly one place:
 *   1. base URL from env (never hardcoded)
 *   2. attach the bearer token
 *   3. unwrap the { success, data } envelope so callers get `data` directly
 *   4. normalise every failure into an ApiError
 *   5. validate the payload against a Zod schema (see request())
 */
import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import type { ZodType, ZodTypeDef } from "zod";
import { ApiError, SchemaError } from "./errors";
import { getToken, clearToken } from "./token";

const BASE_URL = import.meta.env.VITE_API_BASE ?? "/api/v1";
const ENV_NAME = import.meta.env.VITE_APP_ENV ?? import.meta.env.MODE ?? "unknown";

// One-shot boot log so devs can eyeball which backend the app is hitting
// (dev / staging / prod use different `.env.*` files with different
// VITE_API_BASE values). Guarded by `production` where CSP tends to
// forbid random console noise, and by DEV to reduce duplicate logs from
// HMR.
if (import.meta.env.DEV || ENV_NAME !== "production") {
  // eslint-disable-next-line no-console
  console.info(`[env] app=${ENV_NAME}  api=${BASE_URL}`);
}

export const http = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 30_000,
});

http.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/** What every successful endpoint returns (handoff §1). */
interface Envelope<T> {
  success: boolean;
  data: T;
  error?: { code: string; message: string };
  timestamp?: string;
}

/** FastAPI's 422 shape — the documented exception to the envelope. */
interface ValidationBody {
  detail?: Array<{ loc?: unknown[]; msg?: string; type?: string }>;
}

function toApiError(err: unknown): ApiError {
  if (!axios.isAxiosError(err)) {
    return new ApiError("Network request failed", 0, "network_error", err);
  }
  const ax = err as AxiosError<Envelope<unknown> & ValidationBody>;
  const status = ax.response?.status ?? 0;
  const body = ax.response?.data;

  if (status === 0) {
    return new ApiError(
      "Cannot reach the server. Check your connection.",
      0,
      "network_error",
      body,
    );
  }

  // 422 — FastAPI validation, different shape on purpose (handoff §7)
  if (status === 422 && Array.isArray(body?.detail)) {
    const first = body.detail[0];
    const msg = first?.msg ?? "Invalid input";
    return new ApiError(msg, 422, "bad_request", body);
  }

  const code = body?.error?.code ?? "internal_error";
  const message = body?.error?.message ?? ax.message ?? "Request failed";
  return new ApiError(message, status, code, body);
}

/**
 * Make a request and validate the response.
 *
 * @param schema  Zod schema for the `data` payload. Pass one for every call —
 *                it is what turns a silent shape mismatch into a loud error.
 */
export async function request<T>(
  schema: ZodType<T, ZodTypeDef, unknown>,
  config: AxiosRequestConfig,
): Promise<T> {
  const endpoint = `${config.method?.toUpperCase() ?? "GET"} ${config.url ?? ""}`;

  let payload: unknown;
  try {
    const res = await http.request<Envelope<T>>(config);
    const envelope = res.data;

    // The server can return 200 with success:false (handoff §1).
    if (envelope && typeof envelope === "object" && envelope.success === false) {
      const apiErr = new ApiError(
        envelope.error?.message ?? "Request failed",
        res.status,
        envelope.error?.code ?? "internal_error",
        envelope,
      );
      notifyModuleDisabled(apiErr);
      notifyUnauthorized(apiErr, config.url);
      throw apiErr;
    }

    // Unwrap once, here — so no caller ever writes `res.data.data` again.
    payload =
      envelope && typeof envelope === "object" && "data" in envelope
        ? envelope.data
        : envelope;
  } catch (e) {
    if (e instanceof ApiError) throw e;
    const apiErr = toApiError(e);
    notifyModuleDisabled(apiErr);
    notifyUnauthorized(apiErr, config.url);
    throw apiErr;
  }

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new SchemaError(
      endpoint,
      parsed.error.issues.map(
        (i) => `${i.path.join(".") || "(root)"}: ${i.message}`,
      ),
    );
  }
  return parsed.data;
}

/* Thin verb helpers — all validated, all returning `data` directly. */
export const apiGet = <T>(
  schema: ZodType<T, ZodTypeDef, unknown>,
  url: string,
  params?: Record<string, unknown>,
) => request(schema, { method: "GET", url, params });

/**
 * Remove keys whose value is undefined, null, or empty string. Kept
 * separate so callers can preview the pruned object in a debugger before
 * it hits the wire. Preserves `0`, `false`, and empty arrays/objects on
 * purpose — those are meaningful values, not "unset".
 */
export function prune<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === null || v === "") continue;
    (out as Record<string, unknown>)[k] = v;
  }
  return out;
}

/**
 * GET a list-style endpoint that takes a query object.
 *
 * Consolidates the pattern that shows up in every feature api file:
 *   const params: Record<string, unknown> = {};
 *   if (q.search) params["search"] = q.search;
 *   if (q.page) params["page"] = q.page;
 *   return apiGet(schema, url, params);
 *
 * Any field on `query` that's undefined, null, or "" is dropped by prune()
 * — so callers just spread the full query object without conditional
 * assembly. Same return contract as apiGet: response `data` unwrapped,
 * schema-validated, ApiError on failure.
 */
export const apiGetList = <T, Q extends Record<string, unknown>>(
  schema: ZodType<T, ZodTypeDef, unknown>,
  url: string,
  query: Q,
) => apiGet(schema, url, prune(query));

export const apiPost = <T>(schema: ZodType<T, ZodTypeDef, unknown>, url: string, data?: unknown) =>
  request(schema, { method: "POST", url, data: data ?? {} });

export const apiPut = <T>(schema: ZodType<T, ZodTypeDef, unknown>, url: string, data?: unknown) =>
  request(schema, { method: "PUT", url, data });

export const apiPatch = <T>(schema: ZodType<T, ZodTypeDef, unknown>, url: string, data?: unknown) =>
  request(schema, { method: "PATCH", url, data: data ?? {} });

export const apiDelete = <T>(schema: ZodType<T, ZodTypeDef, unknown>, url: string) =>
  request(schema, { method: "DELETE", url });

/**
 * 401 bridge. The client can't null the React user or redirect — that needs
 * auth context — so it clears the token and notifies. The auth layer subscribes
 * and ends the session (null user → RequireAuth redirects to /login). Mirrors
 * the module_disabled bridge below.
 */
type UnauthorizedHandler = () => void;
let unauthorizedHandler: UnauthorizedHandler | null = null;

export function onUnauthorized(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

function notifyUnauthorized(err: ApiError, url: string | undefined): void {
  if (!err.isUnauthorized) return;
  // A 401 on these is an expected local outcome (wrong password, bad OTP), owned
  // by the login form — not a mid-session expiry. Don't trigger a global logout.
  const path = url ?? "";
  if (
    path.includes("/auth/login") ||
    path.includes("/auth/verify-otp") ||
    path.includes("/auth/forgot-password") ||
    path.includes("/auth/reset-password")
  ) {
    return;
  }
  clearToken();
  unauthorizedHandler?.();
}

/**
 * module_disabled bridge (handoff §3). The client can't redirect or refetch
 * /auth/me — that needs React context — so it just notifies. The auth layer
 * subscribes and does the refetch + toast + redirect. Mirrors the 401 pattern.
 */
type ModuleDisabledHandler = (module: string, message: string) => void;
let moduleDisabledHandler: ModuleDisabledHandler | null = null;

export function onModuleDisabled(handler: ModuleDisabledHandler | null): void {
  moduleDisabledHandler = handler;
}

function notifyModuleDisabled(err: ApiError): void {
  if (err.code !== "module_disabled") return;
  // The blocked module name rides along in the error body (handoff §3).
  const body = err.body as { error?: { module?: string } } | undefined;
  const moduleName = body?.error?.module ?? "";
  moduleDisabledHandler?.(moduleName, err.message);
}
